// src/components/NotificationCard.jsx
// Reusable notification card component with responsive design

import React from 'react';

export default function NotificationCard({ 
  children, 
  type = 'info', // 'info', 'success', 'error', 'warning'
  className = '',
  showIcon = true,
  dismissible = false,
  onDismiss,
  ...props 
}) {
  // Define styles based on notification type
  const getTypeStyles = () => {
    switch (type) {
      case 'success':
        return {
          bg: 'bg-emerald-50',
          border: 'border-emerald-200',
          text: 'text-emerald-900',
          icon: 'text-emerald-600',
          iconBg: 'bg-emerald-100'
        };
      case 'error':
        return {
          bg: 'bg-rose-50',
          border: 'border-rose-200',
          text: 'text-rose-900',
          icon: 'text-rose-600',
          iconBg: 'bg-rose-100'
        };
      case 'warning':
        return {
          bg: 'bg-amber-50',
          border: 'border-amber-200',
          text: 'text-amber-900',
          icon: 'text-amber-600',
          iconBg: 'bg-amber-100'
        };
      case 'info':
      default:
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          text: 'text-blue-900',
          icon: 'text-blue-600',
          iconBg: 'bg-blue-100'
        };
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'error':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      case 'warning':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        );
      case 'info':
      default:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  const styles = getTypeStyles();

  return (
    <div
              className={`
          relative
          rounded-xl shadow-lg border-2
          ${styles.bg} ${styles.border} ${styles.text}
          p-4 sm:p-5
          transition-all duration-200 ease-in-out
          hover:shadow-xl
          w-full
          overflow-hidden
          notification-card
          ${className}
        `}
      style={{
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
        maxWidth: '100%',
        boxSizing: 'border-box'
      }}
      {...props}
    >
      {/* Dismiss button */}
      {dismissible && onDismiss && (
        <button
          onClick={onDismiss}
          className="absolute top-3 right-3 p-1 rounded-full hover:bg-white/50 transition-colors"
          aria-label="Dismiss notification"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}

      <div 
        className="flex items-start gap-3 sm:gap-4 w-full"
        style={{
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
          wordBreak: 'break-word',
          maxWidth: '100%'
        }}
      >
        {/* Icon */}
        {showIcon && (
          <div className={`flex-shrink-0 p-2 rounded-full ${styles.iconBg}`}>
            <div className={styles.icon}>
              {getIcon()}
            </div>
          </div>
        )}

        {/* Content */}
        <div 
          className="flex-1 min-w-0 w-full overflow-hidden"
          style={{
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            wordBreak: 'break-word',
            maxWidth: '100%',
            minWidth: '0'
          }}
        >
          <div 
            className="w-full overflow-hidden"
            style={{
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              wordBreak: 'break-word',
              maxWidth: '100%'
            }}
          >
            {children}
          </div>
        </div>
      </div>


    </div>
  );
}

// Specialized notification card variants
export function SuccessCard({ children, ...props }) {
  return (
    <NotificationCard type="success" {...props}>
      {children}
    </NotificationCard>
  );
}

export function ErrorCard({ children, ...props }) {
  return (
    <NotificationCard type="error" {...props}>
      {children}
    </NotificationCard>
  );
}

export function WarningCard({ children, ...props }) {
  return (
    <NotificationCard type="warning" {...props}>
      {children}
    </NotificationCard>
  );
}

export function InfoCard({ children, ...props }) {
  return (
    <NotificationCard type="info" {...props}>
      {children}
    </NotificationCard>
  );
}
